/**
 * The **archer** Arena class (PRD #446 Slice 1, ADR-0237) - the *mobility*
 * class: the one class that fires **while moving** (`firesWhileMoving: true`,
 * the rest keep the ADR-0191 stop-to-cast gate). It trades the fire mage's
 * splash for precision - long-range single-target arrows with bounces, bleeds,
 * poison clouds, crits, and slow-caltrops instead of area denial.
 *
 * This is the twin of {@link ../arenaWeapons} for the archer: a bag of pure,
 * clock-free, world-free **derive-from-base** functions (every effective number
 * is the base constant scaled by the drafted level / passive stat, so repeated
 * application never compounds - the fire mage's discipline) plus the
 * {@link ARCHER_CLASS} def the run captures. The stateful cascade that *calls*
 * these (rolls crits off the seeded rng, mutates the bleed/slow run-state maps,
 * emits the FX events) lives in {@link ../arena} beside the fire cascade - a
 * class def is a pure declaration (no world/rng/clock), so the wiring cannot.
 *
 * Two of these mechanisms are **new to the Arena** (the PRD's per-class
 * mechanism budget): **crit rolls** (Eagle Eye) and **entity DoTs** (Barbed
 * Arrows' bleed). The **bleed table** ({@link bleedDurationTicks} /
 * {@link bleedTickDamage} / {@link ARCHER_BLEED_MAX_STACKS}) is deliberately
 * class-agnostic - the Swordsman's Bleed Attack (Slice 2) reuses it verbatim.
 */

import {
  ARENA_ASCENSION_DAMAGE_PER_PICK,
  ARENA_DAMAGE_PER_STAT_LEVEL,
  ARENA_AREA_PER_STAT_LEVEL,
  ARENA_ZONE_BURN_INTERVAL_TICKS,
  type ArenaModifierId,
  type ArenaModifierLevels,
  type ArenaProjectileKind,
} from '../arenaWeapons';
import type { ArenaClassDef } from './types';

// ---------------------------------------------------------------------------
// The base bow skill (the un-drafted floor the balance guard pins).
// ---------------------------------------------------------------------------

/** The archer's base skill id - also the base bow-shot clip name (UO people
 * anim group 18, `AttackBow`); Heavy Draw swaps it to {@link ARCHER_CROSSBOW_CLIP_ID}. */
export const ARCHER_SKILL_ID = 'shoot-bow';
/** Display name for the run HUD / draft overlay while un-transformed. */
export const ARCHER_SKILL_NAME = 'Bow';
/** Base damage per arrow: one-shots a tier-1 enemy (health 20) - the balance floor. */
export const ARCHER_SKILL_DAMAGE = 20;
/** Base ticks between shots (before the cooldown stat): faster than the mage's,
 * the precision-cadence half of the mobility identity. */
export const ARCHER_SKILL_COOLDOWN_TICKS = 18;
/** Base targeting reach in tiles (Chebyshev) - long, the sniper range. */
export const ARCHER_SKILL_RANGE = 12;

/** The crossbow clip Heavy Draw swaps the bow shot to (UO people anim group 19,
 * `AttackCrossbow`); the server broadcasts it on a Heavy-Draw volley. */
export const ARCHER_CROSSBOW_CLIP_ID = 'shoot-crossbow';

// ---------------------------------------------------------------------------
// The seven archer modifiers, in the fixed draft-pool order (determinism).
// ---------------------------------------------------------------------------

/** Every archer modifier id, in the fixed order the draft pool is built in. A
 * subset of the cross-class {@link ArenaModifierId} union - the archer's own
 * lines, read off the shared `mods` record. */
export const ARCHER_MODIFIER_IDS: readonly ArenaModifierId[] = [
  'double-shot',
  'heavy-draw',
  'ricochet',
  'barbed-arrows',
  'poison-arrows',
  'eagle-eye',
  'caltrops',
];

// --- Double Shot: +1 arrow per level, at the next-nearest foes. -------------

/** Arrows loosed per shot for the drafted Double Shot level (level 5 = 6). */
export function archerArrowCount(mods: ArenaModifierLevels): number {
  return 1 + (mods['double-shot'] ?? 0);
}

/**
 * POE-style "less" multiplier on EVERY arrow while Double Shot is owned - breadth
 * costs a little depth, so spreading fire is a clear win without a round-robin
 * six-arrow volley trivializing a single target (the mage's multi-projectile rule).
 */
export const ARCHER_DOUBLE_SHOT_LESS_FACTOR = 0.85;

// --- Heavy Draw: arrow -> crossbow bolt, +damage per level. -----------------

/** +damage fraction per Heavy Draw level, applied to the base (level 5 = +250%). */
export const ARCHER_HEAVY_DRAW_DAMAGE_PER_LEVEL = 0.5;

/** The arrow's look: a bow arrow until Heavy Draw draws a crossbow bolt. */
export function archerProjectileKind(mods: ArenaModifierLevels): ArenaProjectileKind {
  return (mods['heavy-draw'] ?? 0) >= 1 ? 'bolt' : 'bow-arrow';
}

/** Art size tier for the client (0 = arrow; 1..5 = bolt, growing with Heavy Draw). */
export function archerProjectileSizeTier(mods: ArenaModifierLevels): number {
  return mods['heavy-draw'] ?? 0;
}

/** The people anim-clip the runner plays per shot: the crossbow draw once Heavy
 * Draw is owned, else the bow shot. The server broadcasts it (ADR-0237). */
export function archerCastClip(mods: ArenaModifierLevels): string {
  return (mods['heavy-draw'] ?? 0) >= 1 ? ARCHER_CROSSBOW_CLIP_ID : ARCHER_SKILL_ID;
}

/**
 * Damage one arrow deals: base, grown by Heavy Draw, trimmed by the Double Shot
 * "less" factor while breadth is owned, scaled by the `damage` passive and Power
 * ascensions - the mage's {@link ../arenaWeapons.skillProjectileDamage} shape so
 * the shared passive stack scales the archer identically.
 */
export function archerArrowDamage(
  mods: ArenaModifierLevels,
  damageStatLevel: number,
  powerAscensions = 0,
): number {
  const heavy = 1 + ARCHER_HEAVY_DRAW_DAMAGE_PER_LEVEL * (mods['heavy-draw'] ?? 0);
  const less = (mods['double-shot'] ?? 0) > 0 ? ARCHER_DOUBLE_SHOT_LESS_FACTOR : 1;
  const ascended = 1 + ARENA_ASCENSION_DAMAGE_PER_PICK * powerAscensions;
  return Math.round(
    ARCHER_SKILL_DAMAGE *
      heavy *
      less *
      (1 + ARENA_DAMAGE_PER_STAT_LEVEL * damageStatLevel) *
      ascended,
  );
}

// --- Ricochet: the arrow bounces onward to nearby enemies (chain-hop math). --

/** Max tiles a ricochet can jump from the enemy it leaves (Chebyshev). */
export const ARCHER_RICOCHET_RANGE = 5;

/** Per-bounce damage decay: bounce `b` deals `primary x FACTOR^b`, derived from
 * the primary hit each time (never a running value - the derive-from-base rule). */
export const ARCHER_RICOCHET_DAMAGE_FACTOR = 0.65;

/** Bounces per primary hit for the drafted Ricochet level (0 while unowned). */
export function archerRicochetBounces(mods: ArenaModifierLevels): number {
  return mods.ricochet ?? 0;
}

/** Damage of ricochet bounce `bounce` (1-based) from a primary hit of `primary`,
 * decayed geometrically - always derived from the primary. */
export function archerRicochetDamage(primary: number, bounce: number): number {
  return Math.round(primary * ARCHER_RICOCHET_DAMAGE_FACTOR ** bounce);
}

// --- Barbed Arrows: a bleed DoT (the SHARED bleed table - the Swordsman's
// Bleed Attack, Slice 2, reuses these verbatim). ----------------------------

/** Ticks between bleed applications - the shared ground-zone burn cadence, so a
 * bleed ticks on the same 0.5s beat as every other DoT. */
export const ARCHER_BLEED_INTERVAL_TICKS = ARENA_ZONE_BURN_INTERVAL_TICKS;

/** Ticks a fresh (or refreshed) bleed lasts at level 1. */
export const ARCHER_BLEED_DURATION_BASE_TICKS = 40;
/** Extra bleed ticks per Barbed Arrows level above 1 (level 5 = 6 seconds). */
export const ARCHER_BLEED_DURATION_PER_LEVEL_TICKS = 20;

/** Fraction of the arrow's damage one bleed tick deals per stack at level 1. */
export const ARCHER_BLEED_FRACTION_BASE = 0.15;
/** Extra fraction per Barbed Arrows level above 1 (level 5 = 0.35). */
export const ARCHER_BLEED_FRACTION_PER_LEVEL = 0.05;

/** How many bleed stacks a single wound can hold - repeated barbs deepen it
 * (the {@link ../arenaWeapons.ARENA_MAX_PENDING_METEORS} named-cap discipline). */
export const ARCHER_BLEED_MAX_STACKS = 5;

/** Ticks a bleed of `level` lasts (0 while the modifier is unowned). */
export function bleedDurationTicks(level: number): number {
  if (level <= 0) return 0;
  return ARCHER_BLEED_DURATION_BASE_TICKS + ARCHER_BLEED_DURATION_PER_LEVEL_TICKS * (level - 1);
}

/**
 * Damage ONE bleed stack of `level` ticks for, derived from the hit's `sourceDamage`
 * (the whole damage stack scales the bleed for free; re-deriving is idempotent).
 * The Swordsman feeds its own swing damage + Bleed-Attack level here. Total
 * per-tick damage is this times the wound's live stack count.
 */
export function bleedTickDamage(level: number, sourceDamage: number): number {
  if (level <= 0) return 0;
  const fraction = ARCHER_BLEED_FRACTION_BASE + ARCHER_BLEED_FRACTION_PER_LEVEL * (level - 1);
  return Math.round(sourceDamage * fraction);
}

// --- Poison Arrows: slain foes burst into a lingering poison cloud (ground
// zone - reuses the Scorched Earth zone machinery). -------------------------

/** Poison-cloud radius in tiles at level 1 (Chebyshev), before the area stat. */
export const ARCHER_POISON_RADIUS_BASE = 1;
/** Extra poison radius per Poison Arrows level above 1. */
export const ARCHER_POISON_RADIUS_PER_LEVEL = 0.5;

/** Ticks a poison cloud lingers at level 1. */
export const ARCHER_POISON_DURATION_BASE_TICKS = 40;
/** Extra cloud ticks per level above 1 (level 5 = 6 seconds). */
export const ARCHER_POISON_DURATION_PER_LEVEL_TICKS = 20;

/** Fraction of the killing arrow's damage the cloud deals per burn interval at level 1. */
export const ARCHER_POISON_FRACTION_BASE = 0.2;
/** Extra cloud fraction per level above 1 (level 5 = 0.4). */
export const ARCHER_POISON_FRACTION_PER_LEVEL = 0.05;

/** Poison-cloud radius for the drafted level (0 while unowned), area-stat scaled. */
export function poisonCloudRadius(mods: ArenaModifierLevels, areaStatLevel: number): number {
  const level = mods['poison-arrows'] ?? 0;
  if (level <= 0) return 0;
  return (
    (ARCHER_POISON_RADIUS_BASE + ARCHER_POISON_RADIUS_PER_LEVEL * (level - 1)) *
    (1 + ARENA_AREA_PER_STAT_LEVEL * areaStatLevel)
  );
}

/** Ticks a poison cloud lingers for the drafted level (0 while unowned). */
export function poisonCloudDurationTicks(mods: ArenaModifierLevels): number {
  const level = mods['poison-arrows'] ?? 0;
  if (level <= 0) return 0;
  return (
    ARCHER_POISON_DURATION_BASE_TICKS + ARCHER_POISON_DURATION_PER_LEVEL_TICKS * (level - 1)
  );
}

/** Damage one burn interval of a poison cloud deals, derived from the killing
 * arrow's `sourceDamage` (0 while unowned). */
export function poisonCloudDamage(mods: ArenaModifierLevels, sourceDamage: number): number {
  const level = mods['poison-arrows'] ?? 0;
  if (level <= 0) return 0;
  const fraction = ARCHER_POISON_FRACTION_BASE + ARCHER_POISON_FRACTION_PER_LEVEL * (level - 1);
  return Math.round(sourceDamage * fraction);
}

// --- Eagle Eye: a chance to crit (NEW: seeded crit rolls). ------------------

/** Crit chance per Eagle Eye level (level 5 = 50%). */
export const ARCHER_CRIT_CHANCE_PER_LEVEL = 0.1;
/** Flat crit damage multiplier - a crit hits for double. */
export const ARCHER_CRIT_MULTIPLIER = 2;

/** Crit chance for the drafted Eagle Eye level (0 while unowned - the rng is
 * never drawn while 0, keeping seeded replays byte-identical). */
export function archerCritChance(mods: ArenaModifierLevels): number {
  return ARCHER_CRIT_CHANCE_PER_LEVEL * (mods['eagle-eye'] ?? 0);
}

/** A crit's damage: the base hit doubled (derived, idempotent). */
export function archerCritDamage(baseDamage: number): number {
  return Math.round(baseDamage * ARCHER_CRIT_MULTIPLIER);
}

// --- Caltrops: slow-zones dropped behind the runner (the Scorched trail drop
// pattern, but a SLOW instead of damage). -----------------------------------

/** Ticks a dropped caltrop tile persists at level 1. */
export const ARCHER_CALTROP_DURATION_BASE_TICKS = 40;
/** Extra caltrop-tile ticks per Caltrops level above 1. */
export const ARCHER_CALTROP_DURATION_PER_LEVEL_TICKS = 20;

/** Ticks an enemy stays slowed after its last contact with a caltrop tile. */
export const ARCHER_CALTROP_SLOW_DURATION_TICKS = 30;

/** Added gait multiplier per Caltrops level - a slowed enemy's ms-per-tile is
 * multiplied by `1 + PER_LEVEL x level` (higher ms = slower; level 5 = 1.75x). */
export const ARCHER_CALTROP_SLOW_PER_LEVEL = 0.15;

/** Cap on live caltrop tiles: at the cap the OLDEST is spent early (never the
 * fresh drop - a kiting runner keeps laying their trail), bounding the per-tick
 * enemy-vs-caltrop scan (the {@link ../arena.ARENA_MAX_ZONES} discipline). */
export const ARCHER_MAX_CALTROPS = 64;

/** Ticks a caltrop tile dropped at `level` persists (0 while unowned). */
export function caltropTileDurationTicks(mods: ArenaModifierLevels): number {
  const level = mods.caltrops ?? 0;
  if (level <= 0) return 0;
  return (
    ARCHER_CALTROP_DURATION_BASE_TICKS + ARCHER_CALTROP_DURATION_PER_LEVEL_TICKS * (level - 1)
  );
}

/** The gait multiplier a caltrop of `level` inflicts (1 = no slow while unowned;
 * >1 slows - it multiplies the enemy's ms-per-tile). */
export function caltropSlowFactor(mods: ArenaModifierLevels): number {
  const level = mods.caltrops ?? 0;
  if (level <= 0) return 1;
  return 1 + ARCHER_CALTROP_SLOW_PER_LEVEL * level;
}

// ---------------------------------------------------------------------------
// The class def the run captures.
// ---------------------------------------------------------------------------

/**
 * The archer. `firesWhileMoving: true` is the whole identity - the firing gate
 * ({@link ../arena.fireSkill}) skips the stop-to-cast stillness check for this
 * class alone. Its seven modifier lines feed the draft pool; `keystones: {}`
 * this slice - the four archer keystones (Arrow Storm / Endless Ricochet /
 * Headshot / Hemorrhage) TRAIL as follow-up PRs (unofferable until each lands,
 * the #378 incremental rule). No innate passive - the archer fights on the
 * shared HP baseline (the swordsman's +50% is Slice 2). Its balance floor:
 * one-shots a tier-1 enemy at range 12 with the ~18-tick cadence.
 */
export const ARCHER_CLASS: ArenaClassDef = {
  id: 'archer',
  name: 'Archer',
  skill: {
    id: ARCHER_SKILL_ID,
    name: ARCHER_SKILL_NAME,
    damage: ARCHER_SKILL_DAMAGE,
    cooldownTicks: ARCHER_SKILL_COOLDOWN_TICKS,
    range: ARCHER_SKILL_RANGE,
    firesWhileMoving: true,
  },
  modifierIds: ARCHER_MODIFIER_IDS,
  keystones: {},
  balanceFloor: {
    oneShotTier1: true,
    minSpawnClearFraction: 0.5,
    minReachableFraction: 0.25,
  },
};
